import clsx from 'clsx';
import React from 'react';
import { Listbox } from '@headlessui/react';

import type {
  TWBackgroundProp,
  TWBorderColorProp,
  TWBorderWidthProp,
  TWPositionProp,
} from '../../types/tailwind';
import { Icon } from '../Icon';
import { getTextSize, strictClassNames, type TextSizeType } from '../utils';

export type Option = {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
};

type Props<BackgroundProp, BorderWidthProp, PositionProp, BorderColorProp> = {
  placeholder?: React.ReactNode;
  displayValue: string;
  onChange: (value: string) => void;
  onContextMenu?: (e: React.MouseEvent<HTMLDivElement>) => void;
  options: Option[];
  label?: string;
  labelClassName?: string;
  disabled?: boolean;
  size?: TextSizeType;
  hasError?: boolean;
  noErrorOnOpen?: boolean;
  border?: boolean;
  borderWidth?: TWBorderWidthProp<BorderWidthProp>;
  backgroundColor?: TWBackgroundProp<BackgroundProp>;
  borderColor?: TWBorderColorProp<BorderColorProp>;
  hoverBackgroundColor?: TWBackgroundProp<BackgroundProp>;
  padding?: string;
  height?: string;
  width?: string;
  buttonHeight?: string;
  'data-testid'?: string;
  /** Accessible name for the trigger when the visible label sits outside. */
  'aria-label'?: string;
  extraClassNames?: string;
  dropDownHeightClassName?: string;
  position?: TWPositionProp<PositionProp>;
  showIcon?: boolean;
  overrideDisplayName?: React.ReactNode;
  align?: 'left' | 'right';
  shadow?: string;
};
export const Dropdown = <
  BackgroundProp,
  BorderWidthProp,
  PositionProp,
  BorderColorProp
>({
  options,
  onChange,
  onContextMenu,
  displayValue,
  label,
  labelClassName,
  disabled = false,
  size = 'sm',
  hasError = false,
  noErrorOnOpen = false,
  borderWidth = 'border' as TWBorderWidthProp<BorderWidthProp>,
  border = false,
  backgroundColor,
  borderColor = 'border-canvas' as TWBorderColorProp<BorderColorProp>,
  buttonHeight,
  hoverBackgroundColor = 'bg-negative' as TWBackgroundProp<BackgroundProp>,
  placeholder,
  width: propWidth,
  padding,
  height: heightProp,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  extraClassNames,
  dropDownHeightClassName,
  position = 'relative' as TWPositionProp<PositionProp>,
  showIcon = true,
  overrideDisplayName,
  align = 'left',
  shadow = 'shadow-nice',
}: Props<
  BackgroundProp,
  BorderWidthProp,
  PositionProp,
  BorderColorProp
>): JSX.Element => {
  const lookupDisplayName = (val: string) =>
    options.find((option) => option.value === val)?.label;

  const height = heightProp ?? 'h-fit';
  const textSize = getTextSize(size);
  const width = propWidth ?? '';
  return (
    <Listbox value={displayValue} onChange={onChange}>
      {({ open }) => (
        <div
          className={clsx(`pl-[1/2ch] ${position} ${width}`, extraClassNames)}
          data-testid={dataTestId}
        >
          {label && (
            <Listbox.Label>
              <span className={clsx('text-main-500', labelClassName)}>
                {label}
              </span>
            </Listbox.Label>
          )}
          <div className={position}>
            <Listbox.Button
              aria-disabled={disabled}
              aria-label={ariaLabel}
              data-testid="dropdown-button"
              className={clsx(
                'text-left',
                'group-dropdown',
                disabled
                  ? 'hover:cursor-not-allowed'
                  : `hover:cursor-pointer hover:${hoverBackgroundColor}`,
                backgroundColor ?? 'bg-transparent',
                // Set a border width regardless of the border prop/open state
                borderWidth,
                // No border if border prop is false and dropdown is closed
                !border && ['border-none', 'focus:border-none'],
                // Border if dropdown is open
                open && !hasError && borderColor,
                // Border if border prop is true
                border && !hasError && [borderColor, `focus:${borderColor}`],
                // Border if hasError and border prop is true, but respect noErrorOnOpen
                border &&
                  hasError &&
                  !(open && noErrorOnOpen) &&
                  'border-orange',
                // Border fallback when noErrorOnOpen suppresses error styling
                border && hasError && open && noErrorOnOpen && borderColor,
                width,
                padding,
                buttonHeight ?? height,
                textSize,
                ['focus-visible:outline-main']
              )}
            >
              <div
                className="flex items-center w-full"
                onContextMenu={(e) => {
                  if (!open && onContextMenu) {
                    onContextMenu(e);
                  }
                }}
              >
                <div className={clsx('truncate flex-1 min-w-0', width)}>
                  <span
                    className={strictClassNames(
                      !disabled &&
                        (overrideDisplayName || lookupDisplayName(displayValue))
                        ? ['text-main', 'group-dropdown-hover:text-main']
                        : ['text-main-500']
                    )}
                  >
                    {overrideDisplayName ??
                      (lookupDisplayName(displayValue) ||
                        (placeholder ?? 'Select Option'))}
                  </span>
                </div>
                {showIcon && (
                  <div
                    className="group-dropdown-hover:text-main text-main-500 transition-transform duration-200"
                    style={{ transform: `rotate(${open ? 180 : 0}deg)` }}
                  >
                    <Icon type="down" size={size} />
                  </div>
                )}
              </div>
            </Listbox.Button>

            <Listbox.Options
              data-testid="dropdown-options"
              className={clsx([
                'absolute z-20',
                align === 'right' && 'right-0',
                'scrollbar-hide',
                borderColor,
                'border-t-[0.5px]',
                'hover:cursor-pointer',
                dropDownHeightClassName ?? 'max-h-f14',
                'overflow-y-auto',
                width,
                'focus-visible:outline-main',
                '-mt-[2px]',
                align === 'right' && 'text-right',
                shadow,
              ])}
            >
              {options.map((option) => (
                <Listbox.Option
                  key={option.value}
                  value={option.value}
                  className={clsx(
                    'px-1em',
                    'focus-visible:outline-transparent',
                    'border-b-[0.5px] border-main-300 first:border-t-[0.5px] last:border-b-0',
                    'border-l-[0.5px] border-r-[0.5px] border-box',
                    'flex items-center',
                    height,
                    'hover:bg-negative',
                    option.value === displayValue || option.disabled
                      ? 'text-main-500'
                      : 'text-main',
                    'overflow-y-hidden',
                    backgroundColor ?? 'bg-main-200',
                    padding,
                    option.disabled && 'hover:cursor-not-allowed'
                  )}
                  disabled={option.disabled ?? false}
                >
                  <div
                    data-testid="dropdown-option"
                    aria-disabled={option.disabled ?? false}
                    className={clsx(
                      'flex items-center',
                      textSize,
                      option.disabled && 'hover:cursor-not-allowed'
                    )}
                  >
                    <div className="flex items-center space-x-f1">
                      <div className="truncate">
                        <span>{option.label}</span>
                      </div>
                    </div>
                  </div>
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </div>
        </div>
      )}
    </Listbox>
  );
};
